import { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import type {
  MarketplaceItem,
  MarketplaceCategory,
  FulfillmentType,
  ItemTemplate,
} from "../types";
import {
  CATEGORIES,
  CATEGORY_LABEL_KEYS,
  FULFILLMENT_LABEL_KEYS,
  koinsToEur,
} from "../constants";
import { ImageUploader } from "./ImageUploader";

interface FormState {
  name: string;
  description: string;
  shortDescription: string;
  koinsPrice: string;
  category: MarketplaceCategory;
  scope: "GLOBAL" | "ORGANIZATION";
  organizationId: string;
  fulfillmentType: FulfillmentType;
  fulfillmentInstructions: string;
  totalInventory: string;
  sortOrder: string;
  featured: boolean;
  expiresAt: string;
  autoRefill: boolean;
  refillQuantity: string;
  refillDay: string;
}

function getInitialState(
  item?: MarketplaceItem,
  orgId?: string,
): FormState {
  if (item) {
    return {
      name: item.name,
      description: item.description,
      shortDescription: item.shortDescription ?? "",
      koinsPrice: String(item.koinsPrice),
      category: item.category,
      scope: item.scope,
      organizationId: item.organizationId ?? "",
      fulfillmentType: item.fulfillmentType,
      fulfillmentInstructions: item.fulfillmentInstructions ?? "",
      totalInventory:
        item.totalInventory != null ? String(item.totalInventory) : "",
      sortOrder: String(item.sortOrder ?? 0),
      featured: item.featured,
      expiresAt: item.expiresAt ? item.expiresAt.slice(0, 10) : "",
      autoRefill: item.autoRefill === true,
      refillQuantity: item.refillQuantity
        ? String(item.refillQuantity)
        : "",
      refillDay: item.refillDay
        ? String(item.refillDay)
        : "1",
    };
  }
  return {
    name: "",
    description: "",
    shortDescription: "",
    koinsPrice: "",
    category: "GIFT_CARD",
    scope: orgId ? "ORGANIZATION" : "GLOBAL",
    organizationId: orgId ?? "",
    fulfillmentType: "MANUAL",
    fulfillmentInstructions: "",
    totalInventory: "",
    sortOrder: "0",
    featured: false,
    expiresAt: "",
    autoRefill: false,
    refillQuantity: "",
    refillDay: "1",
  };
}

interface CreateItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    input: Record<string, unknown>,
    imageFile?: File | null,
  ) => void;
  loading: boolean;
  item?: MarketplaceItem;
  templateFill?: ItemTemplate | null;
  currentOrgId?: string | null;
  isOrgAdmin: boolean;
  /** When provided (platform-admin catalog), an org picker is shown for ORGANIZATION scope. */
  organizations?: { _id: string; name: string }[];
}

export function CreateItemDrawer({
  isOpen,
  onClose,
  onSubmit,
  loading,
  item,
  templateFill,
  currentOrgId,
  isOrgAdmin,
  organizations,
}: CreateItemDrawerProps) {
  const { t } = useLocale();
  const [form, setForm] = useState<FormState>(
    getInitialState(item, currentOrgId ?? undefined),
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  const isEdit = Boolean(item);

  // Re-init form when item or open state changes
  useEffect(() => {
    if (isOpen) {
      setForm(getInitialState(item, currentOrgId ?? undefined));
      setImageFile(null);
      setRemoveImage(false);
    }
  }, [isOpen, item, currentOrgId]);

  // Apply template fill
  useEffect(() => {
    if (templateFill) {
      setForm((prev) => ({
        ...prev,
        name: templateFill.name,
        description: templateFill.description,
        shortDescription: templateFill.shortDescription,
        koinsPrice: String(templateFill.koinsPrice),
        category: templateFill.category,
        fulfillmentType: templateFill.fulfillmentType,
        fulfillmentInstructions: templateFill.fulfillmentInstructions,
      }));
    }
  }, [templateFill]);

  const set = useCallback(
    (field: keyof FormState, value: string | boolean) =>
      setForm((prev) => ({ ...prev, [field]: value })),
    [],
  );

  const handleImageSelect = useCallback((file: File) => {
    setImageFile(file);
    setRemoveImage(false);
  }, []);

  const handleImageRemove = useCallback(() => {
    setImageFile(null);
    setRemoveImage(true);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isCreate = !item;

    const input: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim(),
      koinsPrice: parseInt(form.koinsPrice, 10),
      category: form.category,
      fulfillmentType: form.fulfillmentType,
      featured: form.featured,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
    };

    // scope + organizationId only on create (immutable)
    if (isCreate) {
      input.scope = form.scope;
      if (form.scope === "ORGANIZATION" && form.organizationId) {
        input.organizationId = form.organizationId;
      }
    }

    if (form.shortDescription.trim()) {
      input.shortDescription = form.shortDescription.trim();
    } else if (!isCreate) {
      input.shortDescription = "";
    }

    if (form.fulfillmentInstructions.trim()) {
      input.fulfillmentInstructions = form.fulfillmentInstructions.trim();
    }

    if (form.totalInventory.trim()) {
      const inv = parseInt(form.totalInventory, 10);
      if (!isNaN(inv) && inv > 0) {
        input.totalInventory = inv;
      }
    }

    if (form.expiresAt) {
      input.expiresAt = new Date(form.expiresAt).toISOString();
    }

    // If user explicitly removed the image in edit mode
    if (!isCreate && removeImage && !imageFile) {
      input.imageUrl = null;
    }

    // Auto-refill settings
    input.autoRefill = form.autoRefill;
    if (form.autoRefill) {
      const qty = parseInt(form.refillQuantity, 10);
      if (!isNaN(qty) && qty > 0) {
        input.refillQuantity = qty;
      }
      const day = parseInt(form.refillDay, 10);
      if (!isNaN(day) && day >= 1 && day <= 28) {
        input.refillDay = day;
      }
    }

    onSubmit(input, imageFile);
  };

  // When creating an ORGANIZATION-scoped item via the admin picker, an org is required.
  const orgScopeSatisfied =
    isEdit ||
    form.scope !== "ORGANIZATION" ||
    !organizations ||
    Boolean(form.organizationId);

  const isValid =
    form.name.trim() &&
    form.description.trim() &&
    parseInt(form.koinsPrice, 10) > 0 &&
    orgScopeSatisfied;

  const koinsPriceNum = parseInt(form.koinsPrice, 10);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {isEdit ? t("rewards.drawer.editTitle") : t("rewards.drawer.createTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto px-6 py-5"
        >
          {/* Name */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t("rewards.drawer.name")} <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={150}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("rewards.drawer.namePlaceholder")}
              required
            />
          </div>

          {/* Short Description */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t("rewards.drawer.shortDescription")} <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form.shortDescription}
              onChange={(e) => set("shortDescription", e.target.value)}
              maxLength={200}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("rewards.drawer.shortDescriptionPlaceholder")}
            />
          </div>

          {/* Full Description */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t("rewards.drawer.description")} <span className="text-destructive">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={2000}
              rows={3}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("rewards.drawer.descriptionPlaceholder")}
              required
            />
          </div>

          {/* Image Upload */}
          <ImageUploader
            label={t("adminUI.imageUploader.coverImage")}
            currentImageUrl={item?.imageUrl}
            onFileSelect={handleImageSelect}
            onRemove={handleImageRemove}
          />

          {/* Price + Category */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.koinsPrice")} <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={form.koinsPrice}
                onChange={(e) => set("koinsPrice", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={t("rewards.drawer.koinsPricePlaceholder")}
                required
              />
              {koinsPriceNum > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  ≈ €{koinsToEur(koinsPriceNum)}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.category")} <span className="text-destructive">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(CATEGORY_LABEL_KEYS[cat])}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Scope (locked for ORG_ADMIN or edit) */}
          {!isOrgAdmin && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  {t("rewards.drawer.scope")}{" "}
                  {isEdit && (
                    <span className="text-xs text-muted-foreground">
                      {t("rewards.drawer.scopeLocked")}
                    </span>
                  )}
                </label>
                <select
                  value={form.scope}
                  onChange={(e) => set("scope", e.target.value)}
                  disabled={isEdit}
                  className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="GLOBAL">{t("rewards.drawer.scopeGlobal")}</option>
                  <option value="ORGANIZATION">{t("rewards.drawer.scopeOrganization")}</option>
                </select>
              </div>

              {/* Target organization picker — platform-admin catalog only, ORGANIZATION scope, create only */}
              {organizations && form.scope === "ORGANIZATION" && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    {t("rewards.drawer.organization")}{" "}
                    <span className="text-destructive">*</span>{" "}
                    {isEdit && (
                      <span className="text-xs text-muted-foreground">
                        {t("rewards.drawer.scopeLocked")}
                      </span>
                    )}
                  </label>
                  <select
                    value={form.organizationId}
                    onChange={(e) => set("organizationId", e.target.value)}
                    disabled={isEdit}
                    className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">
                      {t("rewards.drawer.organizationPlaceholder")}
                    </option>
                    {organizations.map((org) => (
                      <option key={org._id} value={org._id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {!isEdit && !form.organizationId && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {t("rewards.drawer.organizationRequired")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Fulfillment Type + Inventory */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.fulfillmentType")}
              </label>
              <select
                value={form.fulfillmentType}
                onChange={(e) => set("fulfillmentType", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {(["MANUAL", "AUTOMATIC", "INSTANT"] as const).map((ft) => (
                  <option key={ft} value={ft}>
                    {t(FULFILLMENT_LABEL_KEYS[ft])}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.totalInventory")}
              </label>
              <input
                type="number"
                min={1}
                value={form.totalInventory}
                onChange={(e) => set("totalInventory", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={t("rewards.drawer.inventoryPlaceholder")}
              />
            </div>
          </div>

          {/* Fulfillment Instructions */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t("rewards.drawer.fulfillmentInstructions")}{" "}
              <span className="text-xs text-muted-foreground">{t("rewards.drawer.fulfillmentInstructionsHint")}</span>
            </label>
            <textarea
              value={form.fulfillmentInstructions}
              onChange={(e) => set("fulfillmentInstructions", e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("rewards.drawer.fulfillmentInstructionsPlaceholder")}
            />
          </div>

          {/* Autopilot: Auto-Refill */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.autoRefill}
                onChange={(e) => set("autoRefill", e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("rewards.drawer.autoRefill")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("rewards.drawer.autoRefillHint")}
                </p>
              </div>
            </label>
            {form.autoRefill && (
              <div className="mt-3 grid grid-cols-1 gap-3 pl-7 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-foreground">
                    {t("rewards.drawer.refillQuantity")}
                  </label>
                  <input
                    type="number"
                    value={form.refillQuantity}
                    onChange={(e) => set("refillQuantity", e.target.value)}
                    min={1}
                    placeholder={t("rewards.drawer.refillQuantityPlaceholder")}
                    className="h-8 w-full rounded-md border border-input bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-foreground">
                    {t("rewards.drawer.refillDay")}
                  </label>
                  <input
                    type="number"
                    value={form.refillDay}
                    onChange={(e) => set("refillDay", e.target.value)}
                    min={1}
                    max={28}
                    className="h-8 w-full rounded-md border border-input bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sort Order + Expires + Featured */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.sortOrder")}
              </label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => set("sortOrder", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t("rewards.drawer.expiresAt")}
              </label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => set("expiresAt", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => set("featured", e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
                />
                {t("rewards.featured")}
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading
              ? isEdit
                ? t("rewards.drawer.saving")
                : t("rewards.drawer.creating")
              : isEdit
                ? t("common.save")
                : t("rewards.createReward")}
          </button>
        </div>
      </div>
    </>
  );
}
